import { useEffect, useMemo, useState } from 'react'
import { emptyQuest, type Quest } from './domain/types'
import { QuestEditor } from './ui/QuestEditor/QuestEditor'
import { QuestGraph } from './ui/QuestGraph/QuestGraph'
import { QuestList } from './ui/QuestList/QuestList'
import { SignIn } from './ui/common/SignIn'
import { currentQuest, useStore } from './store'

export default function App() {
  const store = useStore()
  const [newIds, setNewIds] = useState<string[]>([])
  const [proposeOpen, setProposeOpen] = useState(false)
  const [view, setView] = useState<'editor' | 'graph'>('editor')

  useEffect(() => {
    if (store.token && store.status === 'idle') void store.refresh()
  }, [store.token, store.status])

  // Unity's export overwrites the whole directory. Noticing that head moved is the
  // difference between a clean save and a surprise conflict.
  useEffect(() => {
    const onFocus = () => void store.checkRemote()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [store.commitSha, store.token])

  const allQuests = useMemo(() => {
    const committed = [...store.quests.values()].map((entry) => entry.quest)
    const drafted = new Map(committed.map((quest) => [quest.id, quest]))
    for (const [id, draft] of Object.entries(store.drafts)) drafted.set(id, draft.quest)
    return [...drafted.values()]
  }, [store.quests, store.drafts])

  const dirtyIds = useMemo(() => new Set(Object.keys(store.drafts)), [store.drafts])
  const problemIds = useMemo(
    () => new Set(store.graphProblems.flatMap((problem) => problem.questIds)),
    [store.graphProblems],
  )

  if (!store.token) {
    return (
      <SignIn
        onSubmit={(token) => void store.signIn(token)}
        error={store.error}
        busy={store.status === 'loading'}
      />
    )
  }

  if (store.status === 'loading' || (store.status === 'idle' && store.token)) {
    return <Centered>Loading quest data…</Centered>
  }

  if (store.status === 'error') {
    return (
      <Centered>
        <p className="mb-3 text-danger">{store.error}</p>
        <div className="flex gap-2">
          <button
            className="rounded bg-gold px-3 py-1.5 text-sm font-medium text-ink hover:bg-gold-lit"
            onClick={() => void store.refresh()}
          >
            Retry
          </button>
          <button
            className="rounded border border-edge px-3 py-1.5 text-sm"
            onClick={() => store.signOut()}
          >
            Sign out
          </button>
        </div>
      </Centered>
    )
  }

  const catalog = store.catalog
  if (!catalog) return <Centered>No catalog loaded.</Centered>

  const selected = store.selectedId ? currentQuest(store, store.selectedId) : null

  function createQuest() {
    const id = window.prompt(
      'New quest id — this is also the filename (quests/<id>.json).\nNever rename it once it ships; save files store it.',
    )
    if (!id) return
    const trimmed = id.trim()
    if (!trimmed) return
    if (store.quests.has(trimmed) || store.drafts[trimmed]) {
      window.alert(`A quest with id "${trimmed}" already exists.`)
      return
    }
    setNewIds((current) => [...current, trimmed])
    store.edit(emptyQuest(trimmed))
    store.select(trimmed)
  }

  return (
    <div className="flex h-screen flex-col bg-ink">
      <Banners />

      <div className="flex min-h-0 flex-1">
        <QuestList
          quests={allQuests}
          catalog={catalog}
          selectedId={store.selectedId}
          dirtyIds={dirtyIds}
          problemIds={problemIds}
          onSelect={(id) => store.select(id)}
          onCreate={createQuest}
        />

        {view === 'graph' ? (
          <QuestGraph
            quests={allQuests}
            catalog={catalog}
            selectedId={store.selectedId}
            onSelect={(id) => {
              store.select(id)
              setView('editor')
            }}
          />
        ) : selected ? (
          <QuestEditor
            key={selected.id}
            quest={selected}
            catalog={catalog}
            allQuests={allQuests}
            dirty={dirtyIds.has(selected.id)}
            saving={store.saving}
            onChange={(next: Quest) => store.edit(next)}
            onSave={() =>
              void store.save(
                selected.id,
                `${newIds.includes(selected.id) ? 'Add' : 'Update'} quest ${selected.id}`,
              )
            }
            onDiscard={() => {
              store.discardDraft(selected.id)
              if (newIds.includes(selected.id)) {
                setNewIds((current) => current.filter((id) => id !== selected.id))
                store.select(null)
              }
            }}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-dim">
            Pick a quest from the list, or create one.
          </div>
        )}
      </div>

      <footer className="flex items-center gap-3 border-t border-edge bg-ink-lift px-4 py-1.5 text-xs text-dim">
        <div className="flex overflow-hidden rounded border border-edge">
          {(['editor', 'graph'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setView(mode)}
              className={`px-2 py-0.5 ${
                view === mode ? 'bg-gold text-ink' : 'bg-ink-lift hover:bg-ink-high'
              }`}
            >
              {mode === 'editor' ? 'Editor' : 'Chains'}
            </button>
          ))}
        </div>
        <span className="font-mono">{store.commitSha?.slice(0, 7)}</span>
        <span>{store.quests.size} quests</span>
        {dirtyIds.size > 0 && (
          <>
            <span className="font-medium text-warn">{dirtyIds.size} unsaved</span>
            <button
              className="rounded border border-edge px-2 py-0.5 hover:bg-ink-high"
              onClick={() => setProposeOpen(true)}
            >
              Propose all as PR
            </button>
          </>
        )}
        <button
          className="ml-auto hover:underline"
          title="Re-fetch quests and catalogs from GitHub. Does not update the app itself — for that, reload the page."
          onClick={() => void store.refresh()}
        >
          Reload data
        </button>
        <button className="hover:underline" onClick={() => store.signOut()}>
          Sign out
        </button>
      </footer>

      {proposeOpen && <ProposeDialog onClose={() => setProposeOpen(false)} />}
    </div>
  )
}

function Banners() {
  const store = useStore()
  return (
    <>
      {store.remoteMoved && (
        <Banner tone="amber">
          The repo changed on GitHub since you loaded — Unity may have re-exported.
          <button className="ml-2 underline" onClick={() => void store.refresh()}>
            Reload data
          </button>
        </Banner>
      )}
      {store.conflict && <Banner tone="red">{store.conflict}</Banner>}
      {store.broken.length > 0 && (
        <Banner tone="red">
          {store.broken.length} file(s) failed to parse and are not shown:{' '}
          <span className="font-mono">{store.broken.map((b) => b.path).join(', ')}</span>
        </Banner>
      )}
      {store.graphProblems.map((problem, index) => (
        <Banner key={index} tone="amber">
          {problem.message}
        </Banner>
      ))}
    </>
  )
}

function Banner({ tone, children }: { tone: 'amber' | 'red'; children: React.ReactNode }) {
  return (
    <div
      className={`px-4 py-2 text-sm ${
        tone === 'red' ? 'bg-danger-bg text-danger' : 'bg-warn-bg text-warn'
      }`}
    >
      {children}
    </div>
  )
}

function ProposeDialog({ onClose }: { onClose: () => void }) {
  const store = useStore()
  const [title, setTitle] = useState('Balance pass')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-deep/80 p-4">
      <div className="w-full max-w-md rounded-lg bg-ink-lift p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">Propose changes as a pull request</h2>
        <p className="mb-3 text-sm text-dim">
          Puts all {Object.keys(store.drafts).length} unsaved quest(s) on a branch as one
          commit and opens a PR, instead of committing to main.
        </p>
        {result ? (
          <>
            <p className="mb-3 text-sm text-ok">Pull request opened.</p>
            <a
              className="text-sm text-gold underline"
              href={result}
              target="_blank"
              rel="noreferrer"
            >
              {result}
            </a>
            <button className="mt-4 w-full rounded bg-gold py-2 text-sm font-medium text-ink hover:bg-gold-lit" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <input
              className="mb-2 w-full rounded border border-edge px-2 py-1.5 text-sm"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Title"
            />
            <textarea
              className="mb-3 min-h-[6rem] w-full rounded border border-edge px-2 py-1.5 text-sm"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="What changed and why"
            />
            {error && <p className="mb-2 text-sm text-danger">{error}</p>}
            <div className="flex gap-2">
              <button
                className="flex-1 rounded bg-gold py-2 text-sm font-medium text-ink hover:bg-gold-lit disabled:bg-edge disabled:text-dim"
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  setError(null)
                  try {
                    const pull = await store.proposeAll(title, body)
                    setResult(pull.url)
                  } catch (caught) {
                    setError(caught instanceof Error ? caught.message : String(caught))
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {busy ? 'Opening…' : 'Open PR'}
              </button>
              <button className="rounded border border-edge px-4 text-sm" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 text-sm text-dim">
      {children}
    </div>
  )
}
